import { useCallback } from "react";
import api from "../../../../../../services/api";
import {
  ANEXO_ENTITY_TYPE_NODE,
  ANEXO_TYPE_NODE,
} from "../diagramaEquipamentos";

// Monta o multipart para /anexos/createMultipart (etapa 22 do plano) — substitui
// o base64 antigo, sem inflar ~33% o arquivo em memória antes do upload
const montarFormDataAnexo = (arquivo, { nome, type, entityType, entityId }) => {
  const formDataAnexo = new FormData();
  formDataAnexo.append("file", arquivo, nome);
  formDataAnexo.append("type", type);
  formDataAnexo.append("entityType", entityType);
  formDataAnexo.append("entityId", entityId);
  return formDataAnexo;
};

// Hook que concentra o handleSave completo (I3-I6): upsert do circuito + anexos de
// circuito e de nó do diagrama via Promise.allSettled + política de retenção de
// falhas. Extraído de circuito/index.js (Task 5 da decomposição) sem reescrever a
// lógica — mesma ordem de operações, mesmo Promise.allSettled/Promise.all, mesma
// política de retenção.
export default function useCircuitoSave({
  formData,
  setFormData,
  setPrevData,
  validateBlocoAndIp,
  valueRoteamento,
  valueEntregaFinal,
  valueEntregaFinalCliente,
  circuitoAnexos,
  setCircuitoAnexos,
  deletedAnexos,
  setDeletedAnexos,
  revokeNewFilesPreviews,
  getDiagramData,
  resetDiagramData,
  getPendingNodeAnexos,
  refreshNodeAnexosAfterSave,
  serviceId,
  ownerClienteId,
  clienteInfo,
  selectedPopId,
  selectedRegionalId,
  onFormUpdate,
  setSnackbar,
  // Chamada em falha de validação para levar o operador de volta à aba Formulário antes do snackbar de erro
  onTrocarAba = () => {},
  // Loading é estado do circuito (declarado em circuito/index.js); este hook só liga/desliga
  setLoading = () => {},
  // Encerram a própria edição em sucesso, já que não há mais irmão montado para coordenar (Task 7)
  setEditMode = () => {},
  setChanged = () => {},
}) {
  // useCallback: sem memoização, a identidade de handleSave mudava a cada render e o
  // useEffect de report em circuito/index.js (deps [handleSave, ...]) entrava em loop infinito
  const handleSave = useCallback(async () => {
    setLoading(true);
    try {
      if (!validateBlocoAndIp()) {
        throw new Error("Existem campos inválidos");
      }
      // Estado atual do diagrama, enviado junto no mesmo salvamento do circuito
      const diagramData = getDiagramData?.();
      const response = await api.post("/interno/upsert/circuitForm", {
        ...formData,
        // Em grupos econômicos, roteia a escrita ao cliente membro dono do serviço
        clienteId: ownerClienteId || clienteInfo?.id,
        nome: formData?.nome,
        equipRouter: valueRoteamento,
        upload: formData?.speed?.upload,
        equipDelivery: valueEntregaFinal,
        download: formData?.speed?.download,
        hostId: formData?.hostId || null,
        equipLastMile: valueEntregaFinalCliente,
        endClientName: formData?.endClient?.endClientName,
        partnerName: formData?.lastMilePartner?.partnerName,
        endClientEnabled: formData?.endClient?.endClientEnabled,
        partnerEnabled: formData?.lastMilePartner?.partnerEnabled,
        partnerContact: formData?.lastMilePartner?.partnerContact,
        escalationContacts: formData?.lastMilePartner?.escalationContacts,
        selectedRegionalId: selectedRegionalId || null,
        selectedPopId: selectedPopId || null,
        nodes: diagramData?.nodes,
        edges: diagramData?.edges,
      });

      const result = response?.data || {};
      const circuitoId = result?.id || formData?.id;

      // Anexos que não subiram: ficam retidos para o usuário tentar salvar de novo
      let anexosNaoEnviados = [];
      let delecoesNaoAplicadas = [];
      let nomesFalhados = [];
      let totalFalhas = 0;

      if (circuitoId) {
        // Passa o circuitoId definitivo para garantir o entityId composto correto
        // (necessário no primeiro save, quando o prop do diagrama ainda está vazio)
        const pendingNodeAnexos = getPendingNodeAnexos?.(circuitoId) || {
          creates: [],
          deletes: [],
        };
        const delecoesCircuito = (deletedAnexos || []).filter((a) => a?.id);
        const novosAnexos = (circuitoAnexos || []).filter(
          (f) => f.isNew && f.originalFile,
        );

        // Todas as operações de anexo em paralelo; a falha de uma não aborta as outras
        const [resDelCircuito, resNovosCircuito, resDelNode, resNovosNode] =
          await Promise.all([
            Promise.allSettled(
              delecoesCircuito.map((a) => api.post("/anexos/delete", { id: a.id })),
            ),
            Promise.allSettled(
              novosAnexos.map((arquivo) =>
                api.post(
                  "/anexos/createMultipart",
                  montarFormDataAnexo(arquivo.originalFile, {
                    nome: arquivo.name,
                    type: "circuito",
                    entityType: "Circuito",
                    entityId: circuitoId,
                  }),
                ),
              ),
            ),
            Promise.allSettled(
              (pendingNodeAnexos.deletes || []).map((id) =>
                api.post("/anexos/delete", { id }),
              ),
            ),
            Promise.allSettled(
              (pendingNodeAnexos.creates || []).map((anexoNode) =>
                api.post(
                  "/anexos/createMultipart",
                  montarFormDataAnexo(anexoNode.originalFile, {
                    nome: anexoNode.name,
                    type: ANEXO_TYPE_NODE,
                    entityType: ANEXO_ENTITY_TYPE_NODE,
                    // entityId composto (circuitoId:nodeId) vincula o anexo ao circuito
                    entityId: anexoNode.entityId,
                  }),
                ),
              ),
            ),
          ]);

        const falhou = (resultados, indice) =>
          resultados?.[indice]?.status === "rejected";

        // Sem resposta (rede) ou 5xx pode ser tentado de novo; 4xx é definitivo
        const retentavel = (resultado) => {
          const status = resultado?.reason?.response?.status;
          return !status || status >= 500;
        };

        // Uploads que falharam ficam retidos mesmo se 4xx: continuam VISÍVEIS na
        // lista, então o usuário vê o problema e pode remover ou trocar o arquivo.
        anexosNaoEnviados = novosAnexos.filter((_, i) =>
          falhou(resNovosCircuito, i),
        );
        // Deleções são invisíveis na UI: reter uma falha definitiva (ex: anexo já
        // removido, 404) faria todo Salvar seguinte acusar erro sem o usuário ter
        // como limpar. Só reter o que faz sentido tentar de novo.
        delecoesNaoAplicadas = delecoesCircuito.filter(
          (_, i) => falhou(resDelCircuito, i) && retentavel(resDelCircuito[i]),
        );
        nomesFalhados = [
          ...anexosNaoEnviados.map((a) => a?.name),
          ...(pendingNodeAnexos.creates || [])
            .filter((_, i) => falhou(resNovosNode, i))
            .map((a) => a?.name),
        ].filter(Boolean);

        const contarRejeitados = (resultados) =>
          (resultados || []).filter((r) => r?.status === "rejected").length;
        totalFalhas =
          contarRejeitados(resDelCircuito) +
          contarRejeitados(resNovosCircuito) +
          contarRejeitados(resDelNode) +
          contarRejeitados(resNovosNode);

        [resDelCircuito, resNovosCircuito, resDelNode, resNovosNode].forEach(
          (resultados) =>
            (resultados || [])
              .filter((r) => r?.status === "rejected")
              .forEach((r) => console.error("Falha em anexo:", r?.reason)),
        );

        // Ressincroniza SEMPRE, informando o que falhou: o que subiu sai do cache
        // (senão o próximo Salvar reenviaria e duplicaria) e só o que falhou continua
        // pendente para nova tentativa.
        await refreshNodeAnexosAfterSave?.(circuitoId, {
          creates: (pendingNodeAnexos.creates || []).filter((_, i) =>
            falhou(resNovosNode, i),
          ),
          deletes: (pendingNodeAnexos.deletes || []).filter(
            (_, i) => falhou(resDelNode, i) && retentavel(resDelNode[i]),
          ),
        });
      }

      setDeletedAnexos(delecoesNaoAplicadas);
      if (circuitoId) {
        try {
          const resAnexos = await api.post("/anexos/findMany", {
            entityType: "Circuito",
            entityId: circuitoId,
          });
          // Recoloca os que falharam para permitir nova tentativa; revoga o preview só
          // do que subiu com sucesso (o que falhou continua exibido, preview ainda em uso)
          setCircuitoAnexos((prev) => {
            revokeNewFilesPreviews(prev, new Set(anexosNaoEnviados));
            return [...(resAnexos?.data || []), ...anexosNaoEnviados];
          });
        } catch (err) {
          console.error("Erro ao recarregar anexos:", err);
        }
      }

      onFormUpdate(serviceId, result, formData?.tempId);
      setFormData(JSON.parse(JSON.stringify(result)));
      setPrevData(JSON.parse(JSON.stringify(result)));

      // Os avisos se acumulam: um save pode falhar anexo E não sincronizar macro
      const avisos = [];
      if (totalFalhas > 0) {
        avisos.push(
          `${totalFalhas} anexo(s) falharam${
            nomesFalhados.length ? ` (${nomesFalhados.join(", ")})` : ""
          }`,
        );
      }
      if (result?.macrosSyncWarning) {
        avisos.push("as macros de velocidade não foram sincronizadas no Zabbix");
      }

      if (avisos.length) {
        setSnackbar({
          children: `Circuito salvo, mas ${avisos.join(" e ")}.${
            totalFalhas > 0 ? " Tente salvar novamente." : ""
          }`,
          severity: "warning",
        });
      } else {
        setSnackbar({
          children: "Circuito atualizado com sucesso",
          severity: "success",
        });
      }

      // Sucesso encerra a própria edição (falha só de macro não impede, o circuito foi gravado)
      const sucesso = totalFalhas === 0;
      if (sucesso) {
        setEditMode(false);
        setChanged(false);
      }
      return sucesso;
    } catch (error) {
      console.error(error);
      if (error?.message === "Existem campos inválidos") {
        onTrocarAba(0);
        setSnackbar({
          children: `${error?.message}`,
          severity: "error",
        });
      } else {
        setSnackbar({
          children: "Falha ao atualizar circuito",
          severity: "error",
        });
      }
      return false;
    } finally {
      setLoading(false);
    }
  }, [
    formData,
    setFormData,
    setPrevData,
    validateBlocoAndIp,
    valueRoteamento,
    valueEntregaFinal,
    valueEntregaFinalCliente,
    circuitoAnexos,
    setCircuitoAnexos,
    deletedAnexos,
    setDeletedAnexos,
    revokeNewFilesPreviews,
    getDiagramData,
    getPendingNodeAnexos,
    refreshNodeAnexosAfterSave,
    serviceId,
    ownerClienteId,
    clienteInfo,
    selectedPopId,
    selectedRegionalId,
    onFormUpdate,
    setSnackbar,
    onTrocarAba,
    setLoading,
    setEditMode,
    setChanged,
  ]);

  return { handleSave };
}
